'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import GradientOverlay from './GradientOverlay';
import calcStaggeredLeft from './calcStaggeredLeft';
import Chevron from './Chevron';
import cn from '../../utils/cn';

type StaggeredSliderProps = {
  items: React.ReactElement[];
  scrollAmount?: number;
  fade?: boolean;
  className?: string;
  chevronClassName?: string;
};

export default function StaggeredSlider({
  items,
  scrollAmount = 1,
  fade,
  className,
  chevronClassName,
}: StaggeredSliderProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  useEffect(() => {
    const element = sliderRef.current;
    if (!element) {
      return;
    }
    element.scrollTo({ left: 0, behavior: 'smooth' });

    function checkScroll() {
      if (element) {
        const { scrollLeft, scrollWidth, clientWidth } = element;
        setShowLeft(scrollLeft > 0);
        setShowRight(scrollLeft < scrollWidth - clientWidth);
      }
    }

    element.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    checkScroll();

    return () => {
      element.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [items]);

  const scroll = useCallback((count = 1) => {
    if (!sliderRef.current) {
      return;
    }

    sliderRef.current.scrollTo({
      left: calcStaggeredLeft(sliderRef.current, count),
      behavior: 'smooth',
    });
  }, []);

  if (!items || items.length < 1) {
    return null;
  }

  // add chevron left and chevron right buttons
  return (
    <div className="relative flex select-none flex-row items-center">
      <Chevron
        Icon={ChevronLeftIcon}
        className={cn('-left-10', chevronClassName)}
        onClick={() => scroll(-scrollAmount)}
        isVisible={showLeft}
      />
      <Chevron
        Icon={ChevronRightIcon}
        className={cn('-right-10', chevronClassName)}
        onClick={() => scroll(scrollAmount)}
        isVisible={showRight}
      />
      <div className="scrollbar-hide w-full snap-x snap-mandatory overflow-x-auto" ref={sliderRef}>
        <GradientOverlay isVisible={fade && showLeft} isLeft={true} />
        <div className={cn('items-container flex w-full flex-row flex-nowrap', className)}>{items}</div>
        <GradientOverlay isVisible={fade && showRight} isLeft={false} />
      </div>
    </div>
  );
}
